import { Input, Modal, Typography, Form, InputRef, Divider, Skeleton } from 'antd';
import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import media from 'utils/mediaStyle';
import messages from './messages';
import translations from 'translations';
import { FormInput } from 'components/Form';
import { selectVoucherPopup } from 'containers/CheckoutPage/selectors';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import { applyVoucher } from 'containers/CheckoutPage/actions';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { debounce } from 'lodash';
import VoucherCard from './VoucherCard';
import { ContainerState, RootState } from 'containers/CheckoutPage/types';
import Ticket from './Ticket.png';
import UnavailableVoucher from './unavailableVoucher.png';

import { Voucher } from 'types/schema';
import Money from 'components/Money/Money';
import Button from 'components/Button/Button';

const VoucherModal = styled(Modal)`
  max-width: 595px;
  min-width: fit-content;
  border-radius: 16px !important;
  ${media.sm`
    min-width: 595px;
    max-width: auto;
  `}
  border-radius: 8px;
  background: #fff;
  padding-bottom: 0;
  top: 0;
  ${media.sm`
    top: 100px;
  `}

  & .ant-modal-content {
    padding-inline: 16px;
    padding-top: 10px;
    padding-bottom: 0px;
    border-radius: 16px !important;
  }
`;

const Title = styled(Typography.Title)`
  &.ant-typography {
    color: #1d1e20;
    font-weight: 500;
    line-height: 32px; /* 133.333% */
    border: 'none';
    box-shadow: 'none';
    height: 'fit-content';
    padding: 0;
    font-size: 24px;
    margin-bottom: 0;
    text-align: center;
  }
`;

const AForm = styled(Form)``;

const VoucherInputWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

const VoucherInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid #eaeaeb;
  background: #fff;
  max-height: 48px;
  padding: 12px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 510px;
  overflow-y: auto;
  gap: 8px;
  margin-left: -2px;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox, Safari 18.2+, Chromium 121+ */
  &::-webkit-scrollbar {
    display: none; /* Older Safari and Chromium */
  }
`;

const ApplyButton = styled(Button)`
  min-width: 120px;
  font-size: 16px;
  font-weight: 500 !important;
  &.ant-btn.ant-btn-lg {
    height: 48px;
  }
  &:disabled {
    background: #f5f5f4 !important;
    color: #bcbfbb !important;
    cursor: not-allowed;
    border: none;
  }
  &:hover,
  :focus,
  :active {
    opacity: 1;
  }
`;

const SkeletonVoucherCard = styled.div`
  padding-top: 32%;
  position: relative;
  background-image: url(${Ticket});
  background-size: contain;
  background-repeat: no-repeat;
`;

const SkeletonVoucherInfo = styled.div`
  align-self: self-start;
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 6px;
`;

const SkeletonVoucherDate = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: self-end;
  margin-top: 18px;
`;

const ClearButton = styled(Button)`
  color: #1d1e20;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 16px;

  &.ant-btn.ant-btn-lg {
    font-size: 14px;
    height: fit-content;
    padding-inline: 0 !important;
  }

  &.ant-btn-text:not(:disabled):not(.ant-btn-disabled):hover,
  .ant-btn-text:not(:disabled):not(.ant-btn-disabled):focus {
    background-color: transparent;
    color: rgb(47, 144, 79);
  }

  &.ant-btn-text:not(:disabled):not(.ant-btn-disabled):hover *,
  .ant-btn-text:not(:disabled):not(.ant-btn-disabled):focus * {
    background-color: transparent;
    color: rgb(47, 144, 79);
  }
`;

const VoucherUnavailable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(0, 0, 0, 0.54);
  font-size: 16px;
`;

const EmptyVoucherImage = styled.img`
  width: 96px;
  height: 96px;
`;

interface IState {
  voucherState: ContainerState['vouchers'];
}

interface IDispatch {
  onApplyVoucher: (data: any) => void;
}

interface Props extends IState, IDispatch {
  total: number;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const VoucherPopup: React.FC<Props> = ({ voucherState, onApplyVoucher, open, setOpen, total }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm();
  const [submittable, setSubmittable] = React.useState<boolean>(false);
  const voucherCodeValue = Form.useWatch(['voucherForm', 'voucherCode'], form);
  const searchRef = useRef<InputRef | null>(null);
  const getTheSelectedVoucher = useCallback(
    (voucherCode: string) => {
      return voucherState.data.find((voucher) => voucher.code === voucherCode);
    },
    [voucherState.data],
  );
  const debounceCheckVoucher = debounce((value: string) => {
    if (value) {
      setSubmittable(true);
      searchRef.current?.focus();
    } else {
      setSubmittable(false);
    }
  }, 100);

  useEffect(() => {
    if (wrapperRef.current) {
      // Scroll to the middle position of the wrapper
      wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight / 2;
    }
  }, []);

  useEffect(() => {
    if (voucherCodeValue) {
      debounceCheckVoucher(voucherCodeValue);
      if (!voucherState.currentAppliedVoucher && voucherState.status) {
        form.validateFields([['voucherForm', 'voucherCode']]);
      }
    } else {
      setSubmittable(false);
    }
  }, [form, voucherCodeValue, voucherState, debounceCheckVoucher]);

  useEffect(() => {
    if (voucherState.status === 'APPLIED' && voucherState.currentAppliedVoucher) {
      setOpen(false);
      form.resetFields([['voucherForm', 'voucherCode']]);
    }
  }, [voucherState.status, voucherState.currentAppliedVoucher]);

  const handleOnApplyVoucher = (voucher: Voucher) => {
    const payload = {
      code: voucher.code,
      total: total,
    };
    onApplyVoucher(payload);
    setOpen(false);
  };

  const handleApplyVoucherInput = () => {
    const payload = {
      code: voucherCodeValue,
      total: total,
    };
    onApplyVoucher(payload);
  };

  return (
    <VoucherModal
      open={open}
      title={<Title>{translations(messages.title)}</Title>}
      footer={null}
      closable
      onCancel={() => {
        setOpen(false);
        form.resetFields([['voucherForm', 'voucherCode']]);
        if (!voucherState.currentAppliedVoucher) {
          onApplyVoucher({});
        }
      }}
      confirmLoading={voucherState?.loading}
    >
      <Divider style={{ marginBlock: 12, marginInline: '-24px', width: 'calc(100% + 32px)' }} />
      <AForm form={form} layout="vertical" size="large">
        <FormInput
          type={{ type: 'optional' }}
          label={null}
          formId={['voucherForm', 'voucherCode']}
          rules={[
            () => ({
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                switch (voucherState?.status) {
                  case 'APPLIED':
                    // No error if voucher is already applied
                    return Promise.resolve();

                  case 'INVALID':
                    return Promise.reject(<FormattedMessage {...messages.invalidInput} />);

                  case 'EXPIRED':
                    return Promise.reject(<FormattedMessage {...messages.expired} values={{ code: value }} />);

                  case 'NOT_SATISFIED':
                    return Promise.reject(
                      <FormattedMessage
                        {...messages.notSatisfiedInput}
                        values={{
                          code: value,
                          total: <Money>{getTheSelectedVoucher(voucherCodeValue)?.discountMinPrice || total}</Money>,
                        }}
                      />,
                    );

                  default:
                    return Promise.resolve();
                }
              },
            }),
          ]}
          style={{ marginRight: '3px' }}
        >
          <VoucherInputWrapper>
            <VoucherInput
              size="large"
              ref={searchRef}
              type="text"
              onPressEnter={(e) => {
                e.preventDefault();
                handleApplyVoucherInput();
              }}
              // ref={searchRef}
              onChange={(e) => {
                if (!e.target.value) {
                  form.resetFields([['voucherForm', 'voucherCode']]);
                  onApplyVoucher({});
                } else if (voucherState.status !== 'APPLIED' && voucherState.currentAppliedVoucher) {
                  onApplyVoucher({});
                }
              }}
              placeholder={translations(messages.inputPlaceholder)}
            />
            <ApplyButton
              type={submittable ? 'bg-primary' : null}
              onClick={(e) => {
                e.stopPropagation();
                handleApplyVoucherInput();
              }}
              htmlType="submit"
              disabled={!submittable}
            >
              {translations(messages.apply)}
            </ApplyButton>
          </VoucherInputWrapper>
        </FormInput>
        {voucherState.currentAppliedVoucher ? (
          <ClearButton
            onClick={() => {
              form.resetFields([['voucherForm', 'voucherCode']]);
              onApplyVoucher({});
            }}
            type="text"
          >
            <FormattedMessage
              {...messages.clear}
              values={{
                code: <Typography.Text strong>{voucherState.currentAppliedVoucher.code}</Typography.Text>,
              }}
            />
          </ClearButton>
        ) : null}
      </AForm>
      <Wrapper ref={wrapperRef}>
        {voucherState.loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <SkeletonVoucherCard key={`VoucherSkeleton-${index}`}>
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', padding: '24px 16px' }}>
                <SkeletonVoucherInfo>
                  <Skeleton paragraph={{ rows: 1 }} />
                </SkeletonVoucherInfo>
                <SkeletonVoucherDate>
                  <Skeleton paragraph={{ rows: 1 }} />
                  <Skeleton.Button active style={{ width: 88 }} />
                </SkeletonVoucherDate>
              </div>
            </SkeletonVoucherCard>
          ))
        ) : voucherState?.data?.length ? (
          voucherState?.data.map((voucher) => (
            <VoucherCard
              key={voucher.id}
              voucher={voucher}
              handleOnApplyVoucher={handleOnApplyVoucher}
              selectedVoucher={voucherState?.currentAppliedVoucher}
              voucherStatus={voucherState?.status}
              isDisabled={voucher.isDisabled}
            />
          ))
        ) : (
          <VoucherUnavailable>
            <EmptyVoucherImage src={UnavailableVoucher} />
            <FormattedMessage {...messages.invalidTitle} />
          </VoucherUnavailable>
        )}
      </Wrapper>
    </VoucherModal>
  );
};

const mapStateToProps = createStructuredSelector<RootState, IState>({
  voucherState: selectVoucherPopup(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatch => ({
  onApplyVoucher: (data: any) => dispatch(applyVoucher(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VoucherPopup);
